import { Component, LOCALE_ID, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AMORTISATION_CONFIG,
  AmortisationInput,
  PRESET_AVERAGE_CUSTOMER,
  SEGMENT_MARGIN,
  Segment,
  calculateAmortisation,
  cumulativeCashflow,
} from '../core/amortisation.model';
import { toLeadSummary } from '../core/lead.model';
import { RechnerLeadService } from '../core/lead.service';

type FormState = 'idle' | 'sending' | 'done';

@Component({
  selector: 'app-amortisation-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './calculator.component.html',
  styleUrl: './calculator.component.css',
})
export class AmortisationCalculatorComponent {
  private readonly leads = inject(RechnerLeadService);
  private readonly locale = inject(LOCALE_ID);
  private readonly route = inject(ActivatedRoute);

  readonly config = AMORTISATION_CONFIG;

  /** Startzustand = Durchschnittskunde. Wer nichts anfasst, sieht trotzdem eine echte Zahl. */
  readonly input = signal<AmortisationInput>({ ...PRESET_AVERAGE_CUSTOMER });

  constructor() {
    // Vorbelegung aus dem Standort-Rechner: /rechner/amortisation?umsatz=2400
    const umsatz = Number(this.route.snapshot.queryParamMap.get('umsatz'));
    if (Number.isFinite(umsatz) && umsatz > 0) {
      this.patch({ revenue: Math.min(6000, Math.max(300, Math.round(umsatz))) });
    }
  }
  readonly segment = signal<Segment>('direktvermarkter');

  readonly email = signal('');
  readonly formState = signal<FormState>('idle');

  readonly result = computed(() => calculateAmortisation(this.input()));

  /** Zeigt an, ob die Regler noch auf dem Durchschnittskunden stehen. */
  readonly onPreset = computed(() => {
    const i = this.input();
    return (
      i.revenue === PRESET_AVERAGE_CUSTOMER.revenue &&
      i.margin === PRESET_AVERAGE_CUSTOMER.margin &&
      Math.abs(i.basket - PRESET_AVERAGE_CUSTOMER.basket) < 0.005
    );
  });

  /** Anteil der Abschreibungsdauer, der bis zur Amortisation vergeht (0–1). */
  readonly afaProgress = computed(() => {
    const be = this.result().breakevenMonths;
    if (be === null) return 1;
    return Math.min(be / this.config.afaMonths, 1);
  });

  readonly monthsSavedVsAfa = computed(() => {
    const be = this.result().breakevenMonths;
    return be === null ? 0 : Math.round(this.config.afaMonths - be);
  });

  readonly buyAfter12 = computed(() => this.result().monthlySurplus * 12 - this.config.price);
  readonly rentAfter12 = computed(() => this.result().monthlySurplusRented * 12);

  /** Fertiges SVG-Pfadpaar für das Cashflow-Diagramm. */
  readonly chart = computed(() => buildChart(cumulativeCashflow(this.result()), this.locale));

  /** Vorzeichen wird im Template separat gesetzt, damit "+/–" typografisch stimmt. */
  abs(value: number): number {
    return Math.abs(value);
  }

  patch(change: Partial<AmortisationInput>): void {
    this.input.update((current) => ({ ...current, ...change }));
  }

  applyPreset(): void {
    this.input.set({ ...PRESET_AVERAGE_CUSTOMER });
    this.segment.set('direktvermarkter');
  }

  selectSegment(segment: Segment): void {
    this.segment.set(segment);
    this.patch({ margin: SEGMENT_MARGIN[segment] });
  }

  /**
   * Schickt nicht nur die E-Mail, sondern die komplette Rechnung mit.
   * Der Lead kommt damit vorqualifiziert im CRM an; das Backend verschickt
   * die Rechnung als PDF an die angegebene Adresse.
   */
  async submit(): Promise<void> {
    if (this.formState() !== 'idle') return;
    this.formState.set('sending');

    try {
      await this.leads.submit({
        email: this.email(),
        input: this.input(),
        result: toLeadSummary(this.result()),
        segment: this.segment(),
        likelyPrefers: this.prefersPurchase() ? 'kauf' : 'miete',
        source: location.pathname,
        locale: this.locale,
        createdAt: new Date().toISOString(),
      });
    } catch (error) {
      // Der Nutzer soll einen Zustellfehler nicht ausbaden: Wir bestätigen trotzdem
      // und melden den Fehler ans Monitoring. Der Lead ist im Zweifel verloren –
      // deshalb sollte der Endpunkt idempotent sein und Retries vertragen.
      console.error('Lead konnte nicht zugestellt werden', error);
    }

    this.formState.set('done');
  }

  /** Unter dem Kipppunkt (Kaufpreis / Miete ≈ 23 Monate) lohnt der Kauf. */
  private prefersPurchase(): boolean {
    const r = this.result();
    return r.breakevenMonths !== null && r.breakevenMonths < r.buyBeatsRentFromMonth;
  }
}

interface ChartView {
  buyPath: string;
  rentPath: string;
  zeroY: number;
  gridLines: { y: number; label: string }[];
  monthTicks: { x: number; label: string }[];
  breakeven: { x: number; y: number } | null;
}

const CHART = { width: 460, height: 210, left: 44, right: 8, top: 10, bottom: 22, months: 36 };

function buildChart(series: { buy: number[]; rent: number[] }, locale: string): ChartView {
  const all = [...series.buy, ...series.rent];
  let lo = Math.min(0, ...all);
  let hi = Math.max(0, ...all);
  if (hi === lo) hi = lo + 1;
  const pad = (hi - lo) * 0.08;
  lo -= pad;
  hi += pad;

  const x = (m: number) => CHART.left + (m / CHART.months) * (CHART.width - CHART.left - CHART.right);
  const y = (v: number) =>
    CHART.top + (1 - (v - lo) / (hi - lo)) * (CHART.height - CHART.top - CHART.bottom);
  const path = (values: number[]) => 'M' + values.map((v, m) => `${x(m)},${y(v)}`).join(' L');
  const short = (v: number) =>
    Math.abs(v) >= 1000
      ? `${(v / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })}k`
      : `${Math.round(v)}`;

  // Break-even = Schnittpunkt der Kauf-Kurve mit der Nulllinie
  const buy = series.buy;
  let breakeven: { x: number; y: number } | null = null;
  for (let m = 1; m <= CHART.months; m++) {
    if (buy[m - 1] < 0 && buy[m] >= 0) {
      const t = -buy[m - 1] / (buy[m] - buy[m - 1]);
      breakeven = { x: x(m - 1 + t), y: y(0) };
      break;
    }
  }

  return {
    buyPath: path(series.buy),
    rentPath: path(series.rent),
    zeroY: y(0),
    gridLines: [lo, (lo + hi) / 2, hi].map((v) => ({ y: y(v), label: short(v) })),
    monthTicks: [0, 12, 24, 36].map((m) => ({ x: x(m), label: `${m}` })),
    breakeven,
  };
}
